import { Link } from 'react-router-dom'
import { DailyCard } from '@/features/practice/components/DailyCard'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/auth.store'
import { useDashboardStats } from '@/features/dashboard/hooks/useDashboard'
import { useCourses } from '@/features/courses/hooks/useCourses'
import { useSubscription } from '@/features/subscription/hooks/useSubscription'
import { useGamification } from '@/features/gamification/hooks/useGamification'
import { TrialBanner } from '@/features/subscription/components/TrialBanner'
import { SubscriptionExpiredBanner } from '@/features/subscription/components/SubscriptionExpiredBanner'

function BentoSkeleton() {
  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 md:col-span-8 h-44 animate-pulse rounded-3xl bg-surface-container" />
      <div className="col-span-12 md:col-span-4 h-44 animate-pulse rounded-3xl bg-surface-container" />
      <div className="col-span-12 md:col-span-6 h-28 animate-pulse rounded-3xl bg-surface-container" />
      <div className="col-span-12 md:col-span-6 h-28 animate-pulse rounded-3xl bg-surface-container" />
    </div>
  )
}

export function DashboardPage() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const { data: stats, isLoading: statsLoading } = useDashboardStats()
  const { data: coursesData, isLoading: coursesLoading } = useCourses({})
  const { data: subscription } = useSubscription()
  const { data: gamification } = useGamification()
  const courses = coursesData?.data ?? []
  const lastCourse = [...courses].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  )[0]

  // Les XP viennent du domaine Gamification : le tableau de bord les affiche,
  // il ne les recalcule pas.
  const xp = gamification?.xp_total ?? 0
  const level = gamification?.level ?? 0
  const progressPercent = gamification?.progress_percent ?? 0
  const xpInLevel = gamification?.xp_into_level ?? 0

  return (
    <div className="min-h-full bg-surface px-container-px-mobile pb-20 pt-6 md:px-container-px-desktop md:pb-8">
      {subscription?.status === 'trial' && (
        <div className="mb-6">
          <TrialBanner subscription={subscription} />
        </div>
      )}
      {(subscription?.status === 'past_due' || subscription?.status === 'expired') && (
        <div className="mb-6">
          <SubscriptionExpiredBanner subscription={subscription} />
        </div>
      )}

      {/* Bento grid */}
      {statsLoading ? (
        <BentoSkeleton />
      ) : (
        <div className="grid grid-cols-12 gap-4">
          {/* Welcome card */}
          <div className="col-span-12 rounded-3xl bg-primary p-8 text-white shadow-[0px_4px_20px_rgba(0,0,0,0.05)] md:col-span-8">
            <p className="mb-1 font-sans text-sm opacity-80">{t('dashboard.greeting')}</p>
            <h1 className="mb-2 font-display text-3xl font-bold">
              {user?.first_name ?? t('dashboard.default_learner')}
            </h1>
            <p className="mb-6 font-sans text-sm opacity-80">
              {lastCourse
                ? t('dashboard.continue_on', { course: lastCourse.title })
                : t('dashboard.start_first_course')}
            </p>
            <Link
              to={lastCourse ? `/courses/${lastCourse.id}` : '/courses'}
              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-surface-low"
            >
              {lastCourse ? t('dashboard.continue_learning') : t('dashboard.create_course')}
              <svg
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
          </div>

          {/* XP circle card */}
          <div className="col-span-12 flex flex-col items-center justify-center gap-3 rounded-3xl bg-surface-low p-6 md:col-span-4">
            <div
              className="flex h-28 w-28 items-center justify-center rounded-full"
              style={{
                background: `conic-gradient(from 0deg, #712ae2 0%, #00a3c4 ${progressPercent}%, transparent ${progressPercent}% 100%)`,
              }}
            >
              <div className="flex h-20 w-20 flex-col items-center justify-center rounded-full bg-surface-low">
                <span className="font-display text-xl font-bold text-on-surface">{xp}</span>
                <span className="text-[10px] text-on-surface-variant">XP</span>
              </div>
            </div>
            <div className="text-center">
              <p className="font-display text-lg font-semibold text-on-surface">
                {t('dashboard.level', { level })}
              </p>
              <p className="text-xs text-on-surface-variant">
                {t('dashboard.xp_in_level', { current: xpInLevel })}
              </p>
            </div>
          </div>

          {/* Streak card */}
          <div className="col-span-12 flex items-center gap-4 rounded-3xl bg-tertiary/10 p-6 md:col-span-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-tertiary/20">
              <svg viewBox="0 0 24 24" fill="#d08500" className="h-6 w-6" aria-hidden="true">
                <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67z" />
              </svg>
            </div>
            <div>
              <p className="font-display text-2xl font-bold text-tertiary">
                {stats?.streak_days ?? 0}
              </p>
              <p className="font-sans text-sm text-on-surface-variant">
                {t('dashboard.consecutive_days', { count: stats?.streak_days ?? 0 })}
              </p>
              <p className="mt-0.5 font-sans text-xs font-medium text-tertiary">
                {t('dashboard.keep_going')}
              </p>
            </div>
          </div>

          {/* Quiz stats card */}
          <div className="col-span-12 flex items-center gap-4 rounded-3xl bg-secondary/10 p-6 md:col-span-6">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-secondary/20">
              <svg viewBox="0 0 24 24" fill="#712ae2" className="h-6 w-6" aria-hidden="true">
                <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2z" />
              </svg>
            </div>
            <div>
              <p className="font-display text-2xl font-bold text-secondary">
                {stats?.quiz_success_rate ?? 0}%
              </p>
              <p className="font-sans text-sm text-on-surface-variant">
                {t('dashboard.success_rate')}
              </p>
              <p className="mt-0.5 font-sans text-xs font-medium text-secondary">
                {t('dashboard.quizzes_answered', { count: stats?.quizzes_answered ?? 0 })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Practice games section */}
      <section className="mt-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold text-on-surface">
            {t('dashboard.games_title')}
          </h2>
          <Link
            to="/jeux"
            className="inline-flex items-center px-2 py-2.5 text-sm font-medium text-primary hover:underline"
          >
            {t('dashboard.see_all')}
          </Link>
        </div>
        <DailyCard />
      </section>

      {/* Courses section */}
      <section className="mt-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold text-on-surface">
            {t('dashboard.my_courses')}
          </h2>
          <Link
            to="/courses"
            className="inline-flex items-center px-2 py-2.5 text-sm font-medium text-primary hover:underline"
          >
            {t('dashboard.see_all')}
          </Link>
        </div>

        {coursesLoading && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {(['sk-c1', 'sk-c2', 'sk-c3'] as const).map((id) => (
              <div key={id} className="h-36 animate-pulse rounded-3xl bg-surface-container" />
            ))}
          </div>
        )}
        {!coursesLoading && courses.length === 0 && (
          <div className="rounded-3xl bg-surface-low p-8 text-center">
            <p className="mb-4 text-on-surface-variant">{t('dashboard.no_courses')}</p>
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white"
            >
              {t('dashboard.create_first_course')}
            </Link>
          </div>
        )}
        {!coursesLoading && courses.length > 0 && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {courses.slice(0, 6).map((course) => (
              <Link
                to={`/courses/${course.id}`}
                key={course.id}
                className="group rounded-3xl border border-surface-container bg-surface-lowest p-5 shadow-[0px_4px_20px_rgba(0,0,0,0.05)] transition-shadow hover:shadow-[0px_4px_20px_rgba(0,0,0,0.08)]"
              >
                {course.subject && (
                  <span className="mb-3 inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    {course.subject}
                  </span>
                )}
                <h3 className="mb-3 line-clamp-2 font-display text-base font-semibold text-on-surface transition-colors group-hover:text-primary">
                  {course.title}
                </h3>
                <div className="h-1.5 overflow-hidden rounded-full bg-surface-high">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-secondary to-primary transition-all duration-500"
                    style={{ width: `${Math.min(course.sessionCount * 10, 100)}%` }}
                  />
                </div>
                <p className="mt-2 text-xs text-on-surface-variant">
                  {t('dashboard.sessions', { count: course.sessionCount })}
                </p>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
